import { Exclude, Expose } from 'class-transformer'
import type { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument } from './Models'

/**
 * SavedPrompt (MYAC-1106) — one ready-made chat prompt stored under a project
 * (`projects/{pid}/saved_prompts/{id}`). A tier-1 register: members read and
 * write it directly; the body is opaque text that gets prefilled in the chat
 * input. Seeded docs carry `created_by = "mac"`.
 */
@Exclude()
export class SavedPrompt extends _FirebaseDocument {
  @Expose() public name = ''
  @Expose() public description: string | null = null
  @Expose() public body = ''
  @Expose() public order = 0
  @Expose() public enabled = true
  @Expose() public created_by = ''
  @Expose() public updated_by = ''
  @Expose() public schema_version = 1

  collection(): string {
    return 'saved_prompts'
  }

  static fromFirebase(o: DocumentSnapshot): SavedPrompt | null {
    const p = _FirebaseDocument._fromFirebase(SavedPrompt, o)
    if (p && p.description === undefined) p.description = null
    return p
  }

  static collectionPath(projectId: string): string {
    if (!projectId) return ''
    return `projects/${projectId}/saved_prompts`
  }

  static docPath(projectId: string, promptId: string): string {
    if (!projectId || !promptId) return ''
    return `projects/${projectId}/saved_prompts/${promptId}`
  }
}

/** Stable display order: `order` ascending, ties by name (locale-aware). */
export function sortSavedPrompts(list: SavedPrompt[]): SavedPrompt[] {
  return [...list].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
}

/**
 * Field limits the editor enforces (MYAC-1140). `name` and `description` match
 * the Firestore rules; `body` had no limit at all, which matters now that
 * members write their own — a Firestore document caps at 1 MB, and the body is
 * sent verbatim as the chat message, so its length is paid in context on every
 * single use.
 *
 * 50 000 is a guard-rail against a pasted document, not a target: the longest
 * real prompt we have runs ~20 000 characters over ten sections, and the editor
 * warns past `bodySoft` so a member sees the cost before saving.
 */
export const SAVED_PROMPT_LIMITS = {
  name: 120,
  description: 300,
  body: 50_000,
  bodySoft: 20_000,
} as const

/** MAC-seeded prompts are stamped with this author instead of a uid. */
export const SAVED_PROMPT_MAC_AUTHOR = 'mac'

/**
 * True for a prompt MyAuditCorner seeded. Unforgeable from a browser: the
 * create rule requires `created_by == request.auth.uid`, so only the admin SDK
 * can write this author.
 */
export function isMacAuthored(prompt: Pick<SavedPrompt, 'created_by'>): boolean {
  return prompt.created_by === SAVED_PROMPT_MAC_AUTHOR
}

/** Ascending `order` values, spaced so a later insert has room between them. */
export function orderForIndex(index: number): number {
  return (index + 1) * 10
}
